import React, { useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  CircleUserRound,
  Eye,
  MessageCircleMore,
  MoreHorizontal,
  UserRoundCheck,
  UserRoundX,
} from "lucide-react";
import {
  formatFullDateTime,
  formatRelativeTime,
} from "src/utils/DateTimeFormatter";
import { Monogram } from "src/stage/StageUi";
import ConfirmationModal from "src/components/Sidebar/ConfirmationModal";
import TagChip from "src/components/TagChip";

// AUTO-WA-39: the same navy monogram as Chats and Home, so a person looks the same everywhere.
function DirectoryAvatar({ name }) {
  return <Monogram name={name} size={38} />;
}

function SortHeader({
  label,
  ariaLabel = label,
  className = "",
  asc,
  desc,
  sortBy,
  onSortChange,
}) {
  const active = sortBy === asc || sortBy === desc;
  const isAsc = sortBy === asc;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => onSortChange?.(isAsc ? desc : asc)}
        aria-label={`Sort by ${ariaLabel}${active ? (isAsc ? ", ascending" : ", descending") : ""}`}
        className={`group inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[-0.01em] transition-colors ${active ? "text-[#0f6392] dark:text-[#78c7a9]" : "text-[#6f7f7b] hover:text-[#273c37] dark:text-[#839792]"}`}
      >
        {label}
        {!active ? (
          <ChevronsUpDown size={11} className="opacity-35 transition-opacity group-hover:opacity-70" />
        ) : isAsc ? (
          <ChevronUp size={11} />
        ) : (
          <ChevronDown size={11} />
        )}
      </button>
    </div>
  );
}

function ContactStatus({ contact }) {
  const status = contact?.status || "Active";
  const isActive = ["active", "available", "online"].includes(
    String(status).toLowerCase(),
  );

  return (
    <div className="inline-flex items-center gap-2">
      <span className="relative flex h-2 w-2 items-center justify-center">
        {isActive ? (
          <span className="absolute h-2 w-2 rounded-full bg-[#21a878]/20" />
        ) : null}
        <span className={`relative h-1.5 w-1.5 rounded-full ${isActive ? "bg-[#21a878]" : "bg-[#8a9ba8]"}`} />
      </span>
      <span className={`text-[10px] font-semibold ${isActive ? "text-[#35725e] dark:text-[#75c5a5]" : "text-[#788884] dark:text-[#8fa29d]"}`}>
        {status}
      </span>
    </div>
  );
}

function ContactSkeletonRow() {
  return (
    <div className="grid animate-pulse grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 border-b border-[#e1eaef] px-4 py-4 md:grid-cols-[auto_minmax(220px,1fr)_190px_auto] lg:grid-cols-[auto_minmax(240px,1fr)_190px_130px_auto] xl:grid-cols-[auto_minmax(260px,1fr)_200px_130px_130px_auto] sm:px-6 dark:border-[#283a35]">
      <div className="h-4 w-4 rounded bg-[#eef3f6] dark:bg-[#2b3d38]" />
      <div className="flex items-center gap-3.5">
        <div className="h-10 w-10 rounded-[13px] bg-[#eef3f6] dark:bg-[#2b3d38]" />
        <div className="space-y-2">
          <div className="h-3 w-28 rounded bg-[#e3e8e4] dark:bg-[#2b3d38]" />
          <div className="h-2.5 w-20 rounded bg-[#f0f2ef] dark:bg-[#22332e]" />
        </div>
      </div>
      <div className="hidden h-3 w-24 rounded bg-[#eef3f6] md:block dark:bg-[#293b36]" />
      <div className="hidden h-3 w-16 rounded bg-[#eef3f6] lg:block dark:bg-[#293b36]" />
      <div className="hidden h-3 w-16 rounded bg-[#eef3f6] xl:block dark:bg-[#293b36]" />
      <div className="h-8 w-8 rounded-full bg-[#eef3f6] dark:bg-[#293b36]" />
    </div>
  );
}

function EmptyContacts() {
  return (
    <div className="flex min-h-[350px] flex-col items-center justify-center px-6 py-12 text-center">
      <div className="relative mb-5">
        <span className="absolute -inset-3 rounded-[22px] border border-dashed border-[#cdd9d3]" />
        <div className="relative flex h-16 w-16 items-center justify-center rounded-[18px] border border-[#dce4de] bg-[#f4f6f2] text-[#567264] shadow-[0_14px_34px_rgba(55,83,70,0.08)] dark:border-[#355047] dark:bg-[#1b2b26] dark:text-[#8fc7af]">
          <CircleUserRound size={28} strokeWidth={1.45} />
        </div>
      </div>
      <h3 className="text-[15px] font-bold tracking-[-0.02em] text-[#243933] dark:text-[#e1ebe6]">
        No contacts found
      </h3>
      <p className="mt-2 max-w-[360px] text-[11px] leading-5 text-[#5b7180] dark:text-[#82958f]">
        Try another search or clear a filter to bring people back into view.
      </p>
    </div>
  );
}

function ContactsTable({
  contacts = [],
  loading = false,
  selectedContacts = [],
  allCurrentPageSelected = false,
  onSelectAll,
  onSelectContact,
  onAction,
  sortBy = "newest",
  onSortChange,
  perPage = 10,
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const safeContacts = useMemo(
    () => Array.isArray(contacts) ? contacts : [],
    [contacts],
  );

  const handleAction = (action, contact) => {
    setOpenMenu(null);

    if (action === "delete") {
      setContactToDelete(contact);
      setShowDeleteConfirm(true);
      return;
    }

    onAction?.(action, contact);
  };

  return (
    <section className="relative z-10 flex min-h-[390px] flex-col bg-[#fff] dark:bg-[#111b18]">
      <div className="grid shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 border-b border-[#e1eaef] bg-[#f6f9fb] px-4 py-3.5 md:grid-cols-[auto_minmax(220px,1fr)_190px_auto] lg:grid-cols-[auto_minmax(240px,1fr)_190px_130px_auto] xl:grid-cols-[auto_minmax(260px,1fr)_200px_130px_130px_auto] sm:px-6 dark:border-[#2a3d37] dark:bg-[#17231f]">
        <label className="relative flex cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            checked={allCurrentPageSelected}
            onChange={(event) => onSelectAll?.(event.target.checked)}
            aria-label="Select all contacts on this page"
            className="peer sr-only"
          />
          <span className="flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border border-[#c7dce5] bg-white transition-all peer-checked:border-[#147db5] peer-checked:bg-[#147db5] peer-focus:ring-2 peer-focus:ring-[#147db5]/10 dark:border-[#425b53] dark:bg-[#1b2925]">
            <Check size={11} strokeWidth={3} className="text-white" />
          </span>
        </label>

        <SortHeader
          label="Contact"
          asc="name_asc"
          desc="name_desc"
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
        <SortHeader
          label="WhatsApp number"
          className="hidden md:block"
          asc="phone_asc"
          desc="phone_desc"
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
        <SortHeader
          label="Availability"
          ariaLabel="Status"
          className="hidden lg:block"
          asc="status_asc"
          desc="status_desc"
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
        <SortHeader
          label="Added"
          className="hidden xl:block"
          asc="oldest"
          desc="newest"
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
        <span className="w-8" />
      </div>

      <div className="min-h-[340px] flex-1 overflow-y-auto overscroll-contain bg-white dark:bg-[#111b18]">
        {loading ? (
          Array.from({ length: Math.min(perPage, 8) }, (_, index) => (
            <ContactSkeletonRow key={index} />
          ))
        ) : safeContacts.length === 0 ? (
          <EmptyContacts />
        ) : (
          safeContacts.map((contact) => {
            const selected = selectedContacts.includes(contact.id);
            const name = contact?.name || "Unknown contact";
            const phone = contact?.phone_number || "No number";
            const createdAt = contact?.created_at;
            const metaCount = contact?.metadata && typeof contact.metadata === "object"
              ? Object.values(contact.metadata).filter(
                (value) => value !== null && value !== undefined && String(value).trim() !== "",
              ).length
              : 0;

            return (
              <div
                key={contact.id}
                role="button"
                tabIndex={0}
                onClick={() => handleAction("view", contact)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleAction("view", contact);
                }}
                className={`group relative grid min-h-[68px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 border-b border-[#e1eaef] px-4 py-3 transition-all md:grid-cols-[auto_minmax(220px,1fr)_190px_auto] lg:grid-cols-[auto_minmax(240px,1fr)_190px_130px_auto] xl:grid-cols-[auto_minmax(260px,1fr)_200px_130px_130px_auto] sm:px-6 ${selected ? "bg-[#eaf4fa] shadow-[inset_3px_0_0_#147db5] dark:bg-[#1a3029]" : "bg-white hover:bg-[#f6f9fb] dark:bg-[#111b18] dark:hover:bg-[#17251f]"}`}
              >
                <div onClick={(event) => event.stopPropagation()}>
                  <label className="relative flex cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(event) => onSelectContact?.(contact.id, event.target.checked)}
                      aria-label={`Select ${name}`}
                      className="peer sr-only"
                    />
                    <span className="flex h-[18px] w-[18px] items-center justify-center rounded-[5px] border border-[#c7dce5] bg-white transition-all peer-checked:border-[#147db5] peer-checked:bg-[#147db5] peer-focus:ring-2 peer-focus:ring-[#147db5]/10 dark:border-[#40584f] dark:bg-[#1a2924]">
                      <Check
                        size={11}
                        strokeWidth={3}
                        className={`text-white transition-transform ${selected ? "scale-100" : "scale-0"}`}
                      />
                    </span>
                  </label>
                </div>

                <div className="flex min-w-0 items-center gap-3.5">
                  <DirectoryAvatar name={contact?.name} />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="truncate text-[12px] font-bold tracking-[-0.01em] text-[#20352f] dark:text-[#e2ece7]">
                        {name}
                      </p>
                      {metaCount > 0 ? (
                        <span className="shrink-0 text-[9px] font-medium text-[#899691] dark:text-[#82958e]">
                          +{metaCount} field{metaCount === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-1 truncate text-[10px] font-medium tabular-nums text-[#5b7180] md:hidden dark:text-[#81958e]">
                      {phone}
                    </p>

                    {Array.isArray(contact?.tags) && contact.tags.length > 0 ? (
                      <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1">
                        {contact.tags.slice(0, 2).map((tag) => (
                          <TagChip key={tag.id} tag={tag} size="xs" />
                        ))}
                        {contact.tags.length > 2 ? (
                          <span className="text-[8px] font-bold text-[#82918c]">
                            +{contact.tags.length - 2}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="hidden min-w-0 md:block">
                  <p className="truncate text-[11px] font-semibold tabular-nums text-[#344a44] dark:text-[#c6d6cf]">
                    {phone}
                  </p>
                  <p className="mt-1 flex items-center gap-1.5 text-[9px] font-medium text-[#8c9995] dark:text-[#748880]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#21a878]" />
                    WhatsApp
                  </p>
                </div>

                <div className="hidden lg:block">
                  <ContactStatus contact={contact} />
                </div>

                <div className="hidden min-w-0 xl:block" title={formatFullDateTime(createdAt)}>
                  <p className="truncate text-[11px] font-medium text-[#596d67] dark:text-[#afc0b9]">
                    {formatRelativeTime(createdAt)}
                  </p>
                </div>

                <div
                  className="relative flex justify-end"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setOpenMenu((current) => current === contact.id ? null : contact.id)}
                    aria-label="Contact actions"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-[#899792] transition-all hover:bg-[#eeeee9] hover:text-[#354a44] group-hover:bg-[#f2f1ec] dark:hover:bg-[#263932] dark:hover:text-[#c8d8d1]"
                  >
                    <MoreHorizontal size={17} strokeWidth={2} />
                  </button>

                  {openMenu === contact.id ? (
                    <div className="absolute right-0 top-10 z-[110] w-[188px] overflow-hidden rounded-2xl border border-[#dce3df] bg-[#ffffff] p-1.5 shadow-[0_20px_48px_rgba(34,56,47,0.16)] dark:border-[#334a42] dark:bg-[#182621]">
                      {[
                        { action: "view", label: "View details", icon: Eye, tone: "text-[#536862] bg-[#eef1ee]" },
                        { action: "message", label: "Send message", icon: MessageCircleMore, tone: "text-[#28745c] bg-[#e8f4ee]" },
                        { action: "edit", label: "Edit contact", icon: UserRoundCheck, tone: "text-[#416682] bg-[#eaf0f4]" },
                        { action: "delete", label: "Delete contact", icon: UserRoundX, tone: "text-[#b55c58] bg-[#f8ecea]" },
                      ].map((item) => {
                        const Icon = item.icon;

                        return (
                          <button
                            key={item.action}
                            type="button"
                            onClick={() => handleAction(item.action, contact)}
                            className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[10px] font-bold text-[#40544e] transition-colors hover:bg-[#f3f3ef] dark:text-[#c7d7d0] dark:hover:bg-[#22352e]"
                          >
                            <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${item.tone}`}>
                              <Icon size={13} strokeWidth={1.8} />
                            </span>
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ConfirmationModal
        open={showDeleteConfirm}
        title="Delete contact?"
        description={contactToDelete?.name
          ? `Are you sure you want to delete "${contactToDelete.name}"? This contact and its associated data may be removed.`
          : "Are you sure you want to delete this contact? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        icon={UserRoundX}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setContactToDelete(null);
        }}
        onConfirm={async () => {
          try {
            await onAction?.("delete", contactToDelete);
            setShowDeleteConfirm(false);
            setContactToDelete(null);
          } catch (error) {
            console.error("Failed to delete contact:", error);
          }
        }}
      />
    </section>
  );
}

export default ContactsTable;
